import useRpc from "@/src/useRpc";
import LoadingSpinner from "./LoadingSpinner";
import { useEffect, useRef, useState } from "react";
import Modal from "./Modal";
import { refreshCurrentOrg } from "@/src/useCurrentOrg";
import { pluralCount } from "@/lib/string";
import { sendMessage } from "@/src/useMessage";
import ProgressBar from "./ProgressBar";
import rpc, { rpcTask } from "@/src/rpc";
import { useAppRouter } from "@/src/useAppRouter";
import useHighlight from "@/src/useHighlight";
import TopicPicker from "./TopicPicker";
import LegacyContentSettings from "./LegacyContentSettings";
import { sleep } from "@/src/misc";
import SimpleDate from "@/lib/SimpleDate";
import Tooltip from "./Tooltip";
import WebsiteContentSection from "./WebsiteContentSection";
import { useWebsiteScanners } from "@/src/useWebsiteScanner";
import ContentPlansScreen from "./ContentPlansScreen";


export default function ContentSettingsLoader({org}) {
  const [ topicCount ] = useRpc('topics.count')
  const [ contentPlans ] = useRpc('contentPlans.all')

  if (typeof topicCount !== 'number') return <LoadingSpinner size="normal" />

  // Orgs with content plans use the new multi-plan UI
  if (contentPlans?.length > 0) {
    return <ContentPlansScreen org={org} />
  }

  if (topicCount) {
    return <ContentSettings org={org} />
  } else {
    return <LegacyContentSettings org={org} />
  }
}


function ContentSettings({org}) {
  const router = useAppRouter()

  const frequencyRef = useRef(null)
  useHighlight('frequency', frequencyRef)

  const topicsRef = useRef(null)
  useHighlight('topics', topicsRef)

  const [topics, error, reloadTopics] = useRpc('topics.all')
  const [user] = useRpc('users.me')
  const [scanners] = useWebsiteScanners()
  const [editingContentPlan, setEditingContentPlan] = useState(false)
  const [rebuilding, setRebuilding] = useState(false)
  const [rebuildProgress, setRebuildProgress] = useState(0)
  const [localTopics, setLocalTopics] = useState([])
  const [frequencyRange, setFrequencyRange] = useState(org.postsPerWeekMin + '-' + org.postsPerWeekMax)
  const [showZeroOption, setShowZeroOption] = useState(org.postsPerWeekMin === 0 && org.postsPerWeekMax === 0)

  let maxRebuildDate = new SimpleDate().addMonths(3)
  // Set to the last day of the previous month
  maxRebuildDate = maxRebuildDate.addDays(-maxRebuildDate.dayOfMonth)

  const today = new SimpleDate()
  const tomorrow = today.addDays(1)
  const selectedDate = router.query.date ? new SimpleDate(router.query.date) : today
  const startOfMonth = selectedDate.addDays(-selectedDate.dayOfMonth + 1)

  let startDate = null
  if (startOfMonth > maxRebuildDate) {
    startDate = maxRebuildDate.addDays(-maxRebuildDate.dayOfMonth + 1)
  } else if (startOfMonth > today) {
    startDate = startOfMonth
  } else {
    startDate = tomorrow
  }

  const [rebuildStartDate, setRebuildStartDate] = useState(startDate.toString())

  useEffect(() => {
    if (!topics) return
    setLocalTopics(topics)
  }, [topics])


  const {postsPerWeekMin, postsPerWeekMax} = (org || {})
  const min = postsPerWeekMin
  const max = postsPerWeekMax
  let postsPerWeekText = ''
  if (min === max) {
    postsPerWeekText = `${pluralCount(min, 'post')} per week`
  } else {
    postsPerWeekText = `${min}-${pluralCount(max, 'post')} per week`
  }


  function handleCancelUpdate () {
    setEditingContentPlan(false)
    setLocalTopics(topics)
  }


  function handleClickOnFrequency (e) {
    // If the user is holding the Shift key, we want to show the zero option
    const shiftClick = e.shiftKey
    if (shiftClick) setShowZeroOption(true)
  }


  async function handleUpdateCalendar () {

    let startDate = null
    try {
      startDate = new SimpleDate(rebuildStartDate) // Ensure it's a valid date
    } catch (err) {
      console.error(err)
      sendMessage('error', 'Invalid start date')
      return
    }

    setRebuildProgress(0)
    setRebuilding(true)

    try {
      const newFrequency = {
        min: parseInt(frequencyRange.split('-')[0], 10),
        max: parseInt(frequencyRange.split('-')[1], 10)
      }
      await rpc('topics.setFrequency', newFrequency)
      await rpc('topics.save', {topics: localTopics})

      const task = await rpcTask('topics.updateCalendar', {startDate: startDate.toString()})

      await task.done(percent => {
        // The task progress looks like it stalls at 80%, so we'll fake it a bit
        if (percent < 80) {
          setRebuildProgress(percent / 2)
        } else {
          setRebuildProgress(v => Math.min(v + 5, 95))
        }
      })
      setRebuildProgress(100)
      await reloadTopics()
      await refreshCurrentOrg()
      sendMessage('refreshCalendar')
      await sleep(1000) // Wait to let calendar refresh and progress fill up
      router.closePopover()
    } catch (err) {
      console.error(err)
      sendMessage('error', 'Failed to rebuild calendar')
    } finally {
      setEditingContentPlan(false)
      setRebuilding(false)
    }
  }

  return <>
    <div className="flex flex-col bg-f3f3f3 absolute-fill relative overflow-hidden">
      <div className="flex-grow overflow-auto-y">
        <div className="px-5" ref={frequencyRef}>
          { org &&
            <p className="px-1 pt-3 gray text-center">
              {postsPerWeekText} about...
            </p>
          }
        </div>

        { topics ?
            <TopicPicker topics={localTopics} readonly compact/>
          :
          <div className="py-6">
            <LoadingSpinner size="medium" />
          </div>
        }
      </div>

      <div className="text-center bg-white left right py-2 shadow-top z-1">
        <button className="button is-primary" onClick={() => setEditingContentPlan(v => !v)} ref={topicsRef}>
          Edit Plan
        </button>
      </div>
    </div>

    {/* -------------------------------------------------------------------------
      NOTE: We need to render this modal conditionally, instead of using the
      `visible` prop because we need it to use the `selections` prop when the
      customer has already made some selections.
    ------------------------------------------------------------------------- */}
    { editingContentPlan &&
      <Modal visible>
        <div className="flex-col" style={{maxHeight: '86vh'}}>
          { rebuilding ?
            <>
              <div className="p-4 text-center flex-col">
                <LoadingSpinner size="normal" />
                <p className="gray mb-1">
                  Updating calendar...
                </p>
                <ProgressBar value={rebuildProgress}/>
              </div>
            </>
          :
            <>
              <div className="px-5 py-2 shadow bg-white z-up">
                <div className="flex-col sm_flex-row gap-2 flex-justify-center margin-auto">
                  <div className="flex-row flex-center gap-2">
                    <label className="nowrap not-bold">Starting on</label>
                    <input
                      type="date"
                      className="input fit-content"
                      onChange={e => setRebuildStartDate(e.target.value)}
                      value={rebuildStartDate}
                      min={tomorrow.toString()}
                      max={maxRebuildDate.toString()}
                    />
                  </div>
                  <div className="flex-row flex-center gap-2">
                    <span className="nowrap">I want</span> <div className="select">
                      <select className="select" onChange={e => setFrequencyRange(e.target.value)} value={frequencyRange} onClick={handleClickOnFrequency}>
                        {showZeroOption && <option value="0-0">0 posts per week</option>}
                        <option value="1-1">1 post per week</option>
                        <option value="2-3">2-3 posts per week</option>
                        <option value="4-5">4-5 posts per week</option>
                        <option value="6-7">6-7 posts per week</option>
                        <option value="7-7">7 posts per week</option>
                      </select>
                    </div> <span className="hidden sm_contents">about...</span>
                  </div>
                </div>
              </div>

              <div className="flex-grow bg-f3f3f3 overflow-auto-y">
                <TopicPicker onChange={setLocalTopics} topics={localTopics.filter(t => !t.websiteScannerId)} allowNew={!!org.aiEnabled}>
                  { org.aiEnabled && user?.canViewJournalEntries &&
                    <WebsiteContentSection
                      scanners={scanners}
                      localTopics={localTopics.filter(t => !!t.websiteScannerId)}
                      onChange={setLocalTopics}
                      reloadTopics={reloadTopics}
                    />
                  }
                </TopicPicker>
              </div>
              <div className="shadow-top bg-white py-2 z-up">
                <div className="flex-row flex-center gap-2">
                  <button className="button is-danger" onClick={handleUpdateCalendar}>
                    Update Calendar
                    <Tooltip>
                      This will update your calendar from {new SimpleDate(rebuildStartDate).toEnglish({fullMonth: true})} onward, but your custom posts will not be affected.
                    </Tooltip>
                  </button>
                  <button className="button" onClick={handleCancelUpdate}>
                    Cancel
                  </button>
                </div>
              </div>
            </>
          }
        </div>
      </Modal>
    }
  </>
}
